/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CODIGO_POSTAL } from "@utils/expreciones";
import { useFormik } from "formik";
import * as yup from "yup";
import { AddressData, ApiError, PersonaStructure } from "@interfaces/response";
import { RootState, useAppDispatch } from "@store/store";
import { useLazyGetPersonByIdQuery } from "@api/personasApi";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import { useLazyGetByCpQuery } from "@api/postalCodes";
import { useLazyGetAddressTypeQuery } from "@api/catalogs";
import { useLazyGetAddressQuery, usePatchAddressMutation, usePostAddressMutation } from "@api/address";
import { ADDRESS_CODES } from "@helpers/constantes";
import { Address, ColoniaStructure } from "@interfaces/catalogs";
import useFormikErrors from "@hooks/useFormikErrors";

interface AvalAdicionalInfoForm {
  cp: string;
  calle: string;
  num_ext: string;
  num_int: string;
  state_id: string;
  city_id: string;
  colony_id: string;
  state_name: string;
  city_name: string;
  locality_id: string;
  locality_name: string;
  other_neighborhood: string;
}

const InformacionAvalAdicionalP1 = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const avalAdicionalId = useSelector((state: RootState) => state.bofu.aval_adicional_id_information);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);

  const [getPersonById] = useLazyGetPersonByIdQuery();
  const [getAddress] = useLazyGetAddressQuery();
  const [getAddresType] = useLazyGetAddressTypeQuery();
  const [getByCp] = useLazyGetByCpQuery();
  const [postAddress] = usePostAddressMutation();
  const [patchAddress] = usePatchAddressMutation();

  const [isLoading, setIsLoading] = useState(false);
  const [isSavingAddressData, setIsSavingAddressData] = useState<boolean>(false);
  const [avalSelected, setAvalSelected] = useState<PersonaStructure | null>(null);
  const [listColonias, setListColonias] = useState<ColoniaStructure[]>([]);
  const [addresType, setAddresType] = useState<Address | undefined>(undefined);
  const [addressData, setAddressData] = useState<AddressData | undefined>(undefined);

  const formik = useFormik<AvalAdicionalInfoForm>({
    initialValues: {
      cp: "",
      calle: "",
      num_ext: "",
      num_int: "",
      state_id: "",
      city_id: "",
      colony_id: "",
      state_name: "",
      city_name: "",
      locality_id: "",
      locality_name: "",
      other_neighborhood: "",
    },
    validationSchema: yup.object().shape({
      cp: yup.string().required("Campo requerido").matches(CODIGO_POSTAL, "Código Postal inválido"),
      calle: yup.string().required("Campo requerido"),
      num_ext: yup.string().required("Campo requerido"),
      state_name: yup.string().required("Campo requerido"),
      city_name: yup.string(),
      locality_id: yup.string().required("Campo requerido"),
      locality_name: yup.string().required("Campo requerido"),
      colony_id: yup.string().required("Campo requerido"),
      other_neighborhood: yup.string().when("colony_id", {
        is: "-1",
        then: (schema) => schema.required("Campo requerido"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!avalSelected || !addressData) {
          return;
        }
        setSubmitting(true);

        const payload_address = {
          client_details_id: clientDetails.id,
          person_id: avalSelected.id,
          address_type_id: addresType?.id,
          cp: values.cp,
          street: values.calle,
          no_ext: values.num_ext,
          no_int: values.num_int,
          country_id: 1,
          state_id: values.state_id ? +values.state_id : null,
          city_id: values.city_id ? +values.city_id : null,
          locality_id: values.locality_id ? +values.locality_id : null,
          colonia_id: Number(values.colony_id) === -1 ? null : Number(values.colony_id),
          other_neighborhood: values.other_neighborhood,
        };

        if (addressData?.id) {
          const response_update = await patchAddress({
            addressId: addressData.id,
            data: payload_address,
          }).unwrap();
          setAddressData(response_update.data as AddressData);
        } else {
          const response_create = await postAddress(payload_address).unwrap();
          setAddressData(response_create.data);
        }

        navigate("/producto/creditosimple/aval_adicional_buro_4");
      } catch (error) {
        const api_error = error as ApiError;

        Sentry.captureException(error, {
          extra: {
            component: "InformacionAvalAdicionalP1-submit",
          },
        });

        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  const { validateMultipleFields } = useFormikErrors({ formik });

  const fetchLocationData = async (codigoPostal: string, coloniaId?: number) => {
    try {
      if (codigoPostal.length === 5) {
        const value = await getByCp(codigoPostal).unwrap();
        const { colonias, cities, states, localities } = value;
        setListColonias(colonias);

        formik.setFieldValue("state_id", states[0]?.id || "");
        formik.setFieldValue("state_name", states[0]?.name || "");
        formik.setFieldValue("city_id", cities[0]?.id || "");
        formik.setFieldValue("city_name", cities[0]?.name || "");
        formik.setFieldValue("locality_id", localities[0]?.id || "");
        formik.setFieldValue("locality_name", localities[0]?.name || "");

        if (coloniaId) {
          const coloniaSelected = colonias.find((item: ColoniaStructure) => item.id == coloniaId);
          formik.setFieldValue("colony_id", coloniaSelected?.id || "");
        } else {
          formik.setFieldValue("colony_id", colonias[0]?.id || "");
        }

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } else {
        formik.setFieldValue("state_id", "");
        formik.setFieldValue("state_name", "");
        formik.setFieldValue("city_id", "");
        formik.setFieldValue("city_name", "");
        formik.setFieldValue("locality_id", "");
        formik.setFieldValue("locality_name", "");
        formik.setFieldValue("colony_id", "");
      }
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "AvalAdicionalBuroP3-fetchLocationData",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    }
  };

  const saveAddressData = async (
    e:
      | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
      | React.SyntheticEvent<Element, Event>,
  ) => {
    try {
      formik.handleBlur(e);

      if (!avalSelected || isSavingAddressData) {
        return;
      }

      setIsSavingAddressData(true);

      const isValid = validateMultipleFields([
        "cp",
        "calle",
        "num_ext",
        "num_int",
        "state_id",
        "city_id",
        "colony_id",
        "state_name",
        "city_name",
      ]);
      if (!isValid) {
        return;
      }

      const payload_address = {
        client_details_id: clientDetails.id,
        person_id: avalSelected.id,
        address_type_id: addresType?.id,
        cp: formik.values.cp,
        street: formik.values.calle,
        no_ext: formik.values.num_ext,
        no_int: formik.values.num_int,
        country_id: 1,
        state_id: formik.values.state_id ? +formik.values.state_id : null,
        city_id: formik.values.city_id ? +formik.values.city_id : null,
        locality_id: formik.values.locality_id ? +formik.values.locality_id : null,
        colonia_id: Number(formik.values.colony_id) === -1 ? null : Number(formik.values.colony_id),
        other_neighborhood: formik.values.other_neighborhood,
      };

      if (addressData?.id) {
        const response_update = await patchAddress({
          addressId: addressData.id,
          data: payload_address,
        }).unwrap();
        setAddressData(response_update.data as AddressData);
      } else {
        const response_create = await postAddress(payload_address).unwrap();
        setAddressData(response_create.data);
      }
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "AvalAdicionalBuroP3-saveAddressData",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsSavingAddressData(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        if (!avalAdicionalId) {
          navigate("/producto/creditosimple/docs_aval");
          return;
        }

        setIsLoading(true);

        const [aval_selected, res_address_type, res_address] = await Promise.all([
          getPersonById(avalAdicionalId).unwrap(),
          getAddresType().unwrap(),
          getAddress({
            personId: avalAdicionalId,
          }).unwrap(),
        ]);

        if (!aval_selected) {
          navigate("/producto/creditosimple/docs_aval");
          return;
        }

        const address_type = res_address_type.find((item: Address) => item.code === ADDRESS_CODES.particular.code);
        const address = res_address.find((item: AddressData) => item.address_type_id === address_type?.id);

        if (address) {
          formik.setFieldValue("cp", address.cp || "");
          formik.setFieldValue("calle", address.street || "");
          formik.setFieldValue("num_int", address.no_int || "");
          formik.setFieldValue("num_ext", address.no_ext || "");
          formik.setFieldValue("other_neighborhood", address.other_neighborhood || "");

          await fetchLocationData(address.cp, address.colonia_id || -1);
        }

        setAddresType(address_type);
        setAddressData(address);
        setAvalSelected(aval_selected);

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "AvalAdicionalBuroP3-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Información del aval
          </Typography>

          <Typography
            textAlign={"center"}
            color={theme?.palette?.primary?.main}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={600}
            mt={2}
          >
            Te solicitamos ingresar un segundo aval a tu solicitud.
          </Typography>

          <Typography
            mt={2}
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.main}
            fontWeight={600}
          >
            {avalSelected?.name} {avalSelected?.last_name} {avalSelected?.last_name_2}
          </Typography>

          <form onSubmit={formik.handleSubmit} noValidate>
            <FormControl fullWidth>
              <TextField
                id="cp"
                name="cp"
                label="Código Postal"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={saveAddressData}
                onChange={(e) => {
                  const cp = e.target.value.replace(/[^0-9]/g, "");
                  formik.setFieldValue("cp", cp);
                  if (cp.length === 5) {
                    fetchLocationData(cp);
                  }
                }}
                error={formik.touched.cp && Boolean(formik.errors.cp)}
                helperText={formik.touched.cp && formik.errors.cp}
                value={formik.values.cp}
                disabled={isLoading || formik.isSubmitting || isSavingAddressData}
                inputProps={{ maxLength: 5 }}
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                id="calle"
                name="calle"
                label="Calle o avenida"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={saveAddressData}
                onChange={formik.handleChange}
                error={formik.touched.calle && Boolean(formik.errors.calle)}
                helperText={formik.touched.calle && formik.errors.calle}
                value={formik.values.calle}
                disabled={isLoading || formik.isSubmitting || isSavingAddressData}
                inputProps={{ maxLength: 100 }}
              />
            </FormControl>

            <Grid container spacing={1}>
              <Grid item xs={6} sm={6} md={6}>
                <FormControl fullWidth>
                  <TextField
                    id="num_ext"
                    name="num_ext"
                    label="No. Exterior"
                    margin="normal"
                    required
                    fullWidth
                    autoComplete="off"
                    onBlur={saveAddressData}
                    onChange={formik.handleChange}
                    error={formik.touched.num_ext && Boolean(formik.errors.num_ext)}
                    helperText={formik.touched.num_ext && formik.errors.num_ext}
                    value={formik.values.num_ext}
                    disabled={isLoading || formik.isSubmitting || isSavingAddressData}
                    inputProps={{ maxLength: 50 }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={6} md={6}>
                <FormControl fullWidth>
                  <TextField
                    id="num_int"
                    name="num_int"
                    label="No. Interior"
                    margin="normal"
                    required
                    fullWidth
                    autoComplete="off"
                    onBlur={saveAddressData}
                    onChange={formik.handleChange}
                    error={formik.touched.num_int && Boolean(formik.errors.num_int)}
                    helperText={formik.touched.num_int && formik.errors.num_int}
                    value={formik.values.num_int}
                    disabled={isLoading || formik.isSubmitting || isSavingAddressData}
                    inputProps={{ maxLength: 50 }}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="colony_id">Colonia</InputLabel>
              <Select
                labelId="colony_id"
                id="colony_id"
                name="colony_id"
                fullWidth
                required
                label="Colonia"
                value={formik.values.colony_id}
                onBlur={saveAddressData}
                onChange={(event) => {
                  formik.handleChange(event);
                  if (+event.target.value === -1) {
                    formik.setFieldValue("otro", "");
                  }
                }}
                error={formik.touched.colony_id && Boolean(formik.errors.colony_id)}
                disabled={isLoading || formik.isSubmitting || isSavingAddressData}
              >
                <MenuItem value={""}>Selecciona</MenuItem>
                {listColonias.map((colonia: { id: number; name: string }) => (
                  <MenuItem key={colonia.id} value={colonia.id}>
                    {colonia.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.colony_id && formik.errors.colony_id && (
                <FormHelperText>{formik.errors.colony_id}</FormHelperText>
              )}
            </FormControl>

            {String(formik.values.colony_id) === "-1" && (
              <FormControl fullWidth>
                <TextField
                  id="other_neighborhood"
                  name="other_neighborhood"
                  label="Otra Colonia"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onBlur={saveAddressData}
                  onChange={formik.handleChange}
                  error={formik.touched.other_neighborhood && Boolean(formik.errors.other_neighborhood)}
                  helperText={formik.touched.other_neighborhood && formik.errors.other_neighborhood}
                  value={formik.values.other_neighborhood}
                  disabled={isLoading || formik.isSubmitting || isSavingAddressData}
                />
              </FormControl>
            )}

            <FormControl fullWidth>
              <TextField
                id="city_name"
                name="city_name"
                label="Ciudad"
                margin="normal"
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={formik.touched.city_name && Boolean(formik.errors.city_name)}
                helperText={formik.touched.city_name && formik.errors.city_name}
                value={formik.values.city_name}
                disabled
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                id="locality_name"
                name="locality_name"
                label="Municipio"
                margin="normal"
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={formik.touched.locality_name && Boolean(formik.errors.locality_name)}
                helperText={formik.touched.locality_name && formik.errors.locality_name}
                value={formik.values.locality_name}
                disabled
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                id="state_name"
                name="state_name"
                label="Entidad federativa"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={formik.touched.state_name && Boolean(formik.errors.state_name)}
                helperText={formik.touched.state_name && formik.errors.state_name}
                value={formik.values.state_name}
                disabled
              />
            </FormControl>

            <Grid container spacing={1} my={1}>
              <Grid item xs={6} sm={6} md={6}>
                <Button
                  variant="blue_outlined"
                  type="button"
                  fullWidth
                  onClick={() => {
                    navigate("/producto/creditosimple/docs_aval");
                  }}
                  disabled={isLoading || formik.isSubmitting}
                >
                  Regresar al inicio
                </Button>
              </Grid>

              <Grid item xs={6} sm={6} md={6}>
                <Button
                  variant="default"
                  type="submit"
                  fullWidth
                  disabled={isLoading || !formik.isValid || formik.isSubmitting}
                >
                  Continuar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default InformacionAvalAdicionalP1;
